import React, { useEffect, useMemo, useState } from "react";
import { Variable, Info, X } from "lucide-react";
import { getListVariableSources } from "../../apis/contactLists";

/**
 * VariableMappingPanel — compact table UI for CSV / contact-list variable mapping.
 */
const CONTACT_SOURCE_OPTIONS = [
  { value: "field", label: "Contact field" },
  { value: "custom_field", label: "Custom field" },
  { value: "computed", label: "Combination" },
  { value: "literal", label: "Fixed value" },
];

const CSV_SOURCE_OPTIONS = [
  { value: "csv_column", label: "CSV column" },
  { value: "literal", label: "Fixed value" },
];

const EMPTY_RULE_BY_MODE = {
  csv: { source: "csv_column", csv_column: "" },
  contact_list: { source: "field", field: "" },
};

const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[\s\-/]+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");

const SYSTEM_FIELDS = [
  {
    key: "phone",
    label: "Phone number",
    required: true,
    aliases: [
      "phone", "phone_number", "phonenumber", "mobile", "mobile_number",
      "contact", "contact_number", "whatsapp", "whatsapp_number", "wa",
      "wa_number", "number",
    ],
  },
  {
    key: "country_code",
    label: "Country code",
    required: false,
    aliases: ["country_code", "cc", "dial_code", "dialing_code", "isd"],
  },
];

const selectClass =
  "w-full min-w-0 px-2.5 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-400";

const VariableMappingPanel = ({
  templateParams = [],
  mode = "contact_list",
  csvColumns = [],
  listId,
  phoneId,
  value = {},
  onChange,
  inlineErrors = {},
}) => {
  const [catalog, setCatalog] = useState({ defaultFields: [], customFields: [], computed: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (mode !== "contact_list") {
      setCatalog({ defaultFields: [], customFields: [], computed: [] });
      return;
    }
    if (!listId || !phoneId) {
      setCatalog({ defaultFields: [], customFields: [], computed: [] });
      return;
    }
    setLoading(true);
    setError("");
    getListVariableSources(listId, phoneId)
      .then((res) => {
        const data = res.data || {};
        setCatalog({
          defaultFields: Array.isArray(data.defaultFields) ? data.defaultFields : [],
          customFields: Array.isArray(data.customFields) ? data.customFields : [],
          computed: Array.isArray(data.computed) ? data.computed : [],
        });
      })
      .catch((err) => {
        console.error("Failed to load variable sources", err);
        setError(err?.response?.data?.error || "Failed to load contact fields");
      })
      .finally(() => setLoading(false));
  }, [mode, listId, phoneId]);

  useEffect(() => {
    if (mode !== "csv" || !csvColumns?.length) return;

    const next = { ...(value || {}) };
    let changed = false;
    const colByNorm = new Map(csvColumns.map((c) => [norm(c), c]));

    SYSTEM_FIELDS.forEach((sys) => {
      if (next[sys.key] && next[sys.key].source) return;
      const guess = sys.aliases.map((a) => colByNorm.get(norm(a))).find(Boolean);
      if (guess) {
        next[sys.key] = { source: "csv_column", csv_column: guess };
        changed = true;
      }
    });

    (templateParams || []).forEach((p) => {
      if (next[p.key] && next[p.key].source) return;
      const direct = colByNorm.get(norm(p.key));
      const placeholder = p.placeholder ? norm(p.placeholder.replace(/[{}]/g, "")) : null;
      const indirect = placeholder ? colByNorm.get(placeholder) : null;
      const guess = direct || indirect;
      if (guess) {
        next[p.key] = { source: "csv_column", csv_column: guess };
        changed = true;
      }
    });

    if (changed) onChange?.(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, csvColumns?.join("|"), templateParams?.map((p) => p.key).join("|")]);

  const updateRule = (varKey, partial) => {
    const next = { ...(value || {}) };
    next[varKey] = { ...(next[varKey] || EMPTY_RULE_BY_MODE[mode]), ...partial };
    onChange?.(next);
  };

  const removeRule = (varKey) => {
    const next = { ...(value || {}) };
    delete next[varKey];
    onChange?.(next);
  };

  const totalContactSources = catalog.defaultFields.length + catalog.customFields.length;
  const hasTemplateVars = templateParams && templateParams.length > 0;

  if (mode !== "csv" && !hasTemplateVars) return null;

  const heading =
    mode === "csv" ? "Variable mapping" : "Template variables";
  const helper =
    mode === "csv"
      ? "Match each variable to a CSV column. Use “If empty” for a fallback when a row has no value."
      : "Choose a contact field, custom field, or fixed value for each template variable.";

  const showFallbackCol = mode === "csv" || hasTemplateVars;

  const systemRows =
    mode === "csv"
      ? SYSTEM_FIELDS.map((sys) => ({
          key: sys.key,
          param: {
            key: sys.key,
            label: sys.label,
            placeholder: sys.required ? "required" : "",
          },
          hideFallback: true,
        }))
      : [];

  const templateRows = (templateParams || []).map((param) => ({
    key: param.key,
    param,
    hideFallback: false,
  }));

  const rowCount = systemRows.length + templateRows.length;

  return (
    <div className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
            <Variable size={16} className="text-emerald-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-gray-900">{heading}</h3>
            <p className="text-xs text-gray-500 truncate">{helper}</p>
          </div>
        </div>
        {rowCount > 0 && (
          <span className="text-xs font-semibold text-gray-500 tabular-nums shrink-0">
            {rowCount} field{rowCount !== 1 ? "s" : ""}
          </span>
        )}
        {loading && <span className="text-xs text-gray-400">Loading fields…</span>}
      </div>

      {error && (
        <div className="px-4 py-2 text-xs bg-red-50 text-red-700 border-b border-red-100">
          {error}
        </div>
      )}

      {!loading && mode === "contact_list" && totalContactSources === 0 && (
        <div className="px-4 py-2.5 text-xs bg-amber-50 text-amber-800 border-b border-amber-100 flex items-start gap-2">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>
            No contact fields on this list yet. You can still use fixed values, or add fields on
            Contacts.
          </span>
        </div>
      )}

      {mode === "csv" && csvColumns?.length === 0 && (
        <div className="px-4 py-2.5 text-xs bg-amber-50 text-amber-800 border-b border-amber-100 flex items-start gap-2">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>Upload a CSV above to pick columns from your file.</span>
        </div>
      )}

      <div className="overflow-x-auto max-h-[min(520px,55vh)] overflow-y-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide bg-gray-50/90 border-b border-gray-100">
              <th className="px-4 py-2.5 w-[28%]">Variable</th>
              <th className="px-3 py-2.5 w-[18%]">Source</th>
              <th className="px-3 py-2.5">Maps to</th>
              {showFallbackCol && (
                <th className="px-3 py-2.5 w-[18%]">If empty</th>
              )}
              <th className="w-10 px-2 py-2.5" aria-label="Clear" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {mode === "csv" && systemRows.length > 0 && (
              <>
                <tr className="bg-emerald-50/60">
                  <td
                    colSpan={showFallbackCol ? 5 : 4}
                    className="px-4 py-1.5 text-[11px] font-bold text-emerald-800 uppercase tracking-wide"
                  >
                    Required columns
                  </td>
                </tr>
                {systemRows.map(({ key, param, hideFallback }) => (
                  <RuleRow
                    key={key}
                    mode={mode}
                    csvColumns={csvColumns}
                    catalog={catalog}
                    param={param}
                    rule={value?.[key] || { ...EMPTY_RULE_BY_MODE.csv }}
                    inlineError={inlineErrors[key]}
                    onChange={(partial) => updateRule(key, partial)}
                    onClear={() => removeRule(key)}
                    hideFallback={hideFallback}
                    showFallbackCol={showFallbackCol}
                  />
                ))}
              </>
            )}

            {hasTemplateVars && (
              <>
                {mode === "csv" && systemRows.length > 0 && (
                  <tr className="bg-gray-50/80">
                    <td
                      colSpan={showFallbackCol ? 5 : 4}
                      className="px-4 py-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-wide"
                    >
                      Template variables
                    </td>
                  </tr>
                )}
                {templateRows.map(({ key, param, hideFallback }) => (
                  <RuleRow
                    key={key}
                    mode={mode}
                    csvColumns={csvColumns}
                    catalog={catalog}
                    param={param}
                    rule={value?.[key] || { ...EMPTY_RULE_BY_MODE[mode] }}
                    inlineError={inlineErrors[key]}
                    onChange={(partial) => updateRule(key, partial)}
                    onClear={() => removeRule(key)}
                    hideFallback={hideFallback}
                    showFallbackCol={showFallbackCol}
                  />
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RuleRow = ({
  mode,
  csvColumns,
  catalog,
  param,
  rule,
  onChange,
  onClear,
  inlineError,
  hideFallback,
  showFallbackCol,
}) => {
  const sourceOptions = useMemo(
    () => (mode === "csv" ? CSV_SOURCE_OPTIONS : CONTACT_SOURCE_OPTIONS),
    [mode]
  );

  const renderSourceInput = () => {
    if (rule.source === "csv_column") {
      return (
        <select
          value={rule.csv_column || ""}
          onChange={(e) => onChange({ csv_column: e.target.value })}
          className={selectClass}
          disabled={!csvColumns?.length}
        >
          <option value="">
            {csvColumns?.length ? "Select column…" : "Upload CSV first"}
          </option>
          {(csvColumns || []).map((col) => (
            <option key={col} value={col}>
              {col}
            </option>
          ))}
        </select>
      );
    }
    if (rule.source === "field") {
      return (
        <select
          value={rule.field || ""}
          onChange={(e) => onChange({ field: e.target.value })}
          className={selectClass}
        >
          <option value="">Select field…</option>
          {catalog.defaultFields.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
      );
    }
    if (rule.source === "custom_field") {
      return (
        <select
          value={rule.custom_field_key || ""}
          onChange={(e) => onChange({ custom_field_key: e.target.value })}
          className={selectClass}
          disabled={catalog.customFields.length === 0}
        >
          <option value="">
            {catalog.customFields.length === 0 ? "No custom fields" : "Select custom field…"}
          </option>
          {catalog.customFields.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
      );
    }
    if (rule.source === "computed") {
      return (
        <select
          value={rule.expression || ""}
          onChange={(e) => onChange({ expression: e.target.value })}
          className={selectClass}
        >
          <option value="">Select combination…</option>
          {catalog.computed.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        type="text"
        value={rule.value || ""}
        onChange={(e) => onChange({ value: e.target.value })}
        placeholder="Same value for every contact"
        className={selectClass}
      />
    );
  };

  const isRequired = param.placeholder === "required";

  return (
    <tr className={inlineError ? "bg-red-50/40" : "hover:bg-gray-50/60 transition-colors"}>
      <td className="px-4 py-2 align-middle">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-gray-900 truncate text-sm">
            {param.label}
            {isRequired && <span className="text-red-500 ml-0.5">*</span>}
          </span>
          {param.placeholder && param.placeholder !== "required" && (
            <code className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded shrink-0">
              {param.placeholder}
            </code>
          )}
        </div>
        {inlineError && (
          <p className="text-[11px] font-medium text-red-600 mt-0.5 leading-tight">{inlineError}</p>
        )}
      </td>
      <td className="px-3 py-2 align-middle">
        <select
          value={rule.source}
          onChange={(e) => {
            const next = { source: e.target.value };
            if (e.target.value === "field") next.field = "";
            if (e.target.value === "custom_field") next.custom_field_key = "";
            if (e.target.value === "csv_column") next.csv_column = "";
            if (e.target.value === "computed") next.expression = "";
            if (e.target.value === "literal") next.value = "";
            onChange(next);
          }}
          className={selectClass}
        >
          {sourceOptions.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2 align-middle">{renderSourceInput()}</td>
      {showFallbackCol && (
        <td className="px-3 py-2 align-middle">
          {!hideFallback ? (
            <input
              type="text"
              value={rule.fallback || ""}
              onChange={(e) => onChange({ fallback: e.target.value })}
              placeholder="Fallback value"
              title="Used when the mapped value is empty"
              className={selectClass}
            />
          ) : (
            <span className="text-xs text-gray-400 px-1">—</span>
          )}
        </td>
      )}
      <td className="px-2 py-2 align-middle text-center">
        <button
          type="button"
          onClick={onClear}
          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          title="Clear mapping"
        >
          <X size={14} />
        </button>
      </td>
    </tr>
  );
};

export default VariableMappingPanel;
